import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import {
    useCreateSCIMConfiguration,
    useDeleteSCIMConfiguration,
    useUpdateSCIMConfiguration,
    type PlainSCIMConfiguration,
} from "@/queries/scim-queries";
import { useListSSOConfigurations } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useEffect, useState, type FC } from "react";
import { Controller, useForm } from "react-hook-form";

export type ConfigureSCIMProps = {
    organizationId: string;
    config?: PlainSCIMConfiguration;
    onClose?: () => void;
};

type SCIMForm = {
    name: string;
    ssoConfigurationId: string;
};

type ConfigureSCIMState = "configure" | "display-token" | "display-regenerated" | "confirm-delete";

export const ConfigureSCIM: FC<ConfigureSCIMProps> = ({ organizationId, config, onClose }) => {
    const { toast } = useToast();
    const [state, setState] = useState<ConfigureSCIMState>("configure");
    const [token, setToken] = useState<string>("");

    const isEdit = !!config;

    const { data: ssoConfigs = [] } = useListSSOConfigurations();

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid, isDirty },
    } = useForm<SCIMForm>({
        mode: "all",
        defaultValues: config
            ? {
                  name: config.name || "",
                  ssoConfigurationId: config.ssoConfigurationId || "",
              }
            : {
                  name: "",
                  ssoConfigurationId: "",
              },
    });

    const createMutation = useCreateSCIMConfiguration();
    const updateMutation = useUpdateSCIMConfiguration();
    const deleteMutation = useDeleteSCIMConfiguration();

    const [serverError, setServerError] = useState<string>();

    // Warn users before closing when token is displayed
    useTokenDisplayWarning(state);

    const handleCloseTokenDisplay = useCallback(() => {
        const confirmed = window.confirm("Have you saved the token? It will not be shown again.");
        if (confirmed) {
            setToken(""); // Clear token from state
            onClose?.();
        }
    }, [onClose]);

    const handleOnSubmit = useCallback(
        async (data: SCIMForm) => {
            setServerError(undefined);
            try {
                if (isEdit) {
                    // Update existing configuration
                    await updateMutation.mutateAsync({
                        scimConfigurationId: config.id,
                        name: data.name,
                        ssoConfigurationId: data.ssoConfigurationId || undefined,
                    });
                    toast({
                        title: "SCIM configuration updated",
                    });
                    onClose?.();
                } else {
                    // Create new configuration - returns token!
                    if (!data.ssoConfigurationId) {
                        setServerError("SSO configuration is required");
                        return;
                    }
                    const result = await createMutation.mutateAsync({
                        organizationId,
                        name: data.name,
                        ssoConfigurationId: data.ssoConfigurationId,
                    });
                    setToken(result.token);
                    setState("display-token");
                }
            } catch (error) {
                const description = formatError(error);
                setServerError(description);
                toast({
                    title: isEdit ? "Failed to update SCIM configuration" : "Failed to create SCIM configuration",
                    description,
                });
            }
        },
        [config?.id, createMutation, isEdit, onClose, organizationId, toast, updateMutation],
    );

    const handleDelete = useCallback(async () => {
        if (!config?.id) return;
        try {
            await deleteMutation.mutateAsync(config.id);
            toast({
                title: "SCIM configuration deleted",
            });
            onClose?.();
        } catch (error) {
            toast({
                title: "Failed to delete SCIM configuration",
                description: formatError(error),
            });
        }
    }, [config?.id, deleteMutation, onClose, toast]);

    // Configure state - Create/Edit form
    if (state === "configure") {
        return (
            <>
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit SCIM Configuration" : "New SCIM Configuration"}</DialogTitle>
                    <DialogDescription>
                        {isEdit
                            ? "Update your SCIM configuration settings"
                            : "Configure SCIM provisioning to automate user management"}
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <form id="scim-form" onSubmit={handleSubmit(handleOnSubmit)} className="flex flex-col gap-4">
                        <InputField
                            label="Configuration Name"
                            hint="A descriptive name for this SCIM configuration"
                            error={errors?.name?.message}
                        >
                            <Input
                                {...register("name", {
                                    required: "Name is required",
                                    minLength: { value: 3, message: "Name must be at least 3 characters" },
                                    maxLength: { value: 128, message: "Name must be 128 characters or fewer" },
                                })}
                                placeholder="Production SCIM"
                            />
                        </InputField>

                        <InputField
                            label="Link to SSO Configuration"
                            hint="Associate this SCIM config with an SSO configuration for user authentication"
                            error={errors?.ssoConfigurationId?.message}
                        >
                            <Controller
                                name="ssoConfigurationId"
                                control={control}
                                rules={{
                                    required: !isEdit ? "SSO configuration is required" : false,
                                }}
                                render={({ field }) => (
                                    <Select
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        disabled={ssoConfigs.length === 0}
                                    >
                                        <SelectTrigger loading={false}>
                                            <SelectValue
                                                placeholder={
                                                    ssoConfigs.length === 0
                                                        ? "No SSO configurations available"
                                                        : "Select SSO configuration"
                                                }
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ssoConfigs.map((sso) => (
                                                <SelectItem key={sso.id} value={sso.id}>
                                                    {sso.displayName || sso.issuerUrl}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </InputField>

                        {ssoConfigs.length === 0 && (
                            <Banner variant="info" text="You need to create an SSO configuration first before setting up SCIM." />
                        )}

                        {serverError && <Banner variant="danger" text={serverError} />}
                    </form>
                </DialogBody>

                <DialogFooter>
                    {isEdit && (
                        <Button
                            variant="destructive"
                            onClick={() => setState("confirm-delete")}
                            data-tracking-id="show-delete-scim-modal"
                        >
                            Delete
                        </Button>
                    )}
                    <div className="flex-grow" />
                    <DialogClose asChild>
                        <Button variant="outline" data-tracking-id="cancel-configure-scim">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        form="scim-form"
                        variant="primary"
                        disabled={!isValid || (!isDirty && isEdit) || ssoConfigs.length === 0}
                        loading={createMutation.isPending || updateMutation.isPending}
                        data-tracking-id="submit-configure-scim"
                    >
                        {isEdit ? "Update" : "Create"}
                    </Button>
                </DialogFooter>
            </>
        );
    }

    // Confirm delete state
    if (state === "confirm-delete") {
        return (
            <>
                <DialogHeader>
                    <DialogTitle>Delete SCIM Configuration?</DialogTitle>
                    <DialogDescription />
                </DialogHeader>
                <DialogBody className="flex flex-col gap-2">
                    <Text className="text-base">This will permanently delete the SCIM configuration.</Text>
                    <Text className="text-base font-medium">This action cannot be undone.</Text>
                    <Text className="text-base text-content-secondary">
                        Users provisioned via SCIM will remain in the organization but will no longer be managed through SCIM.
                    </Text>
                </DialogBody>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setState("configure")} data-tracking-id="cancel-delete-scim">
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        loading={deleteMutation.isPending}
                        disabled={deleteMutation.isPending}
                        data-tracking-id="confirm-delete-scim"
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </>
        );
    }

    // Token display state - shown after creation or regeneration
    if (state === "display-token" || state === "display-regenerated") {
        const isRegenerated = state === "display-regenerated";
        const scimEndpoint = `${window.location.origin}/scim/v2`;

        return (
            <>
                <DialogHeader>
                    <DialogTitle>{isRegenerated ? "Token Regenerated" : "SCIM Configuration Created"}</DialogTitle>
                    <DialogDescription>
                        Save this information securely. The token will only be shown once.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    <Banner variant="warning" text="Store this token securely. It will not be shown again." />

                    <InputField label="SCIM Endpoint URL" hint="Use this URL in your identity provider's SCIM settings">
                        <CopyableInput
                            value={scimEndpoint}
                            disabled
                            data-tracking-id="copy-scim-endpoint-token-display"
                        />
                    </InputField>

                    <InputField label="Bearer Token" hint="This token authenticates SCIM requests from your identity provider">
                        <CopyableInput
                            value={token}
                            disabled
                            type="password"
                            data-tracking-id="copy-scim-token"
                        />
                    </InputField>

                    {isRegenerated && (
                        <Banner
                            variant="info"
                            text="The previous token has been invalidated and will no longer work."
                        />
                    )}
                </DialogBody>

                <DialogFooter>
                    <Button variant="primary" onClick={handleCloseTokenDisplay} data-tracking-id="close-scim-token-display">
                        Done
                    </Button>
                </DialogFooter>
            </>
        );
    }

    return null;
};

/**
 * Hook to warn users before closing/navigating away when token is displayed
 */
const useTokenDisplayWarning = (state: ConfigureSCIMState) => {
    const shouldWarn = state === "display-token" || state === "display-regenerated";

    useEffect(() => {
        if (!shouldWarn) return;

        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "Have you saved your SCIM token?";
        };

        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [shouldWarn]);
};
